import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Instagram, Link2, Search, SlidersHorizontal, Youtube } from "lucide-react";

const CREATOR_CATEGORIES = [
  "All", "Personal", "Business", "Influencer", "Food Blogger", "Event Promoter",
  "Artist", "DJ", "Dancer", "Venue", "Community Group", "Sponsor",
];

const CREATOR_INTERESTS = [
  "All", "Garba/Navratri", "Music", "Comedy", "Theatre", "Food", "Family/Kids",
  "Business Networking", "Religious/Community", "Nightlife", "Wellness",
];

const first = (values, fallback = "All") => Array.isArray(values) && values.length ? values[0] : fallback;

export default function Creators() {
  const navigate = useNavigate();
  const { languages, cities } = useApp();
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("All UK");
  const [language, setLanguage] = useState("All");
  const [category, setCategory] = useState("All");
  const [interest, setInterest] = useState("All");
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  const languageOptions = useMemo(() => ["All", ...(languages || []).map((l) => l.name).filter(Boolean)], [languages]);
  const cityOptions = useMemo(() => ["All UK", ...(cities || []).filter(Boolean)], [cities]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/creators", {
      params: {
        q: query || undefined,
        city: city !== "All UK" ? city : undefined,
        language: language !== "All" ? language : undefined,
        category: category !== "All" ? category : undefined,
        interest: interest !== "All" ? interest : undefined,
      },
    }).then((r) => {
      if (alive) setCreators(r.data || []);
    }).catch(() => {
      if (alive) setCreators([]);
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [query, city, language, category, interest]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="page-shell py-8 space-y-6">
        <section className="rounded-2xl border border-border p-5 md:p-7">
          <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Community</p>
          <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-display text-3xl md:text-5xl font-black">Namaste Creators</h1>
              <p className="mt-2 max-w-3xl text-sm md:text-base text-muted-foreground">
                Discover influencers, food bloggers, event promoters, artists, venues and community pages by language, location and event interests.
              </p>
            </div>
            <Button asChild className="rounded-full">
              <a href="/profile">Create your profile</a>
            </Button>
          </div>
        </section>

        <section className="rounded-2xl border border-border p-4 md:p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-black text-primary">
            <SlidersHorizontal className="h-4 w-4" />
            Find creators
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-5">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, handle, bio..."
                className="pl-9"
              />
            </div>
            <Select value={city} onChange={setCity} options={cityOptions} />
            <Select value={language} onChange={setLanguage} options={languageOptions} />
            <Select value={category} onChange={setCategory} options={CREATOR_CATEGORIES} />
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
            {CREATOR_INTERESTS.map((value) => (
              <button
                key={value}
                onClick={() => setInterest(value)}
                className={`pill shrink-0 ${interest === value ? "pill-active" : ""}`}
              >
                {value}
              </button>
            ))}
          </div>
        </section>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-56 rounded-2xl border border-border bg-muted/40" />)}
          </div>
        ) : creators.length === 0 ? (
          <section className="rounded-2xl border border-border p-10 text-center">
            <p className="font-display text-2xl font-black">No creators found</p>
            <p className="mt-2 text-sm text-muted-foreground">Try a wider city, language or interest filter.</p>
          </section>
        ) : (
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {creators.map((creator) => <CreatorCard key={creator.user_id} creator={creator} onOpen={() => navigate(`/creators/${creator.user_id}`)} />)}
          </section>
        )}
      </main>
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-input bg-background px-3 text-sm"
    >
      {[...new Set(options)].map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  );
}

function CreatorCard({ creator, onOpen }) {
  const name = creator.display_name || creator.name || "Namaste creator";
  const categories = creator.creator_categories || [];
  const languages = creator.creator_languages || [];
  const locations = creator.creator_locations || [];
  return (
    <article className="rounded-2xl border border-border p-5 transition hover:-translate-y-0.5 hover:border-primary/50">
      <button type="button" onClick={onOpen} className="w-full text-left">
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={creator.profile_image_url || creator.picture} />
          <AvatarFallback>{name.slice(0, 1)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-black leading-tight">{name}</h2>
          {creator.instagram_handle && <p className="text-sm text-primary font-bold">{creator.instagram_handle}</p>}
          <p className="mt-1 text-xs text-muted-foreground">
            {first(locations, "UK")} · {languages.slice(0, 3).join(", ") || "All languages"}
          </p>
        </div>
      </div>
      </button>
      {categories.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {categories.slice(0, 4).map((item) => <Badge key={item} variant="secondary">{item}</Badge>)}
        </div>
      )}
      {creator.bio && <p className="mt-4 line-clamp-3 text-sm text-muted-foreground">{creator.bio}</p>}
      <div className="mt-5 flex flex-wrap gap-2">
        <Button size="sm" onClick={onOpen} className="rounded-full">View profile</Button>
        {creator.instagram_url && <Button size="sm" variant="outline" asChild className="rounded-full"><a href={creator.instagram_url} target="_blank" rel="noreferrer"><Instagram className="mr-2 h-4 w-4" />Instagram</a></Button>}
        {creator.youtube_url && <Button size="sm" variant="outline" asChild className="rounded-full"><a href={creator.youtube_url} target="_blank" rel="noreferrer"><Youtube className="mr-2 h-4 w-4" />YouTube</a></Button>}
        {creator.website_url && <Button size="sm" variant="outline" asChild className="rounded-full"><a href={creator.website_url} target="_blank" rel="noreferrer"><Link2 className="mr-2 h-4 w-4" />Website</a></Button>}
      </div>
    </article>
  );
}
